import React from 'react';
import {
  HStack, VStack, Pressable, Icon, Box, Text, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import {
  ChevronRight, ChevronDown, Check, TriangleAlert, Trophy, Sparkles, Lock,
} from 'lucide-react-native';
import { Svg, Circle, Path } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import type { Task } from '@/common/api/models/task';
import { getFlatEvaluationDimensions } from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import {
  calculateOralExamPerformance,
  calculateTaskPerformance,
  convertEvaluationsToPerformances,
  formatProjectedGradeDisplay,
} from '../../utils/gradingConversions';
import { isOralExamTask, resolveOralExamDimensionKeys } from '../../utils/evaluationFormMapping';
import type { TaskFormEvaluation } from '../../types';

// Column width for each dimension in the grid - generous spacing
const DIMENSION_COL_WIDTH = 100;
// Width for the aggregate percentage column
const AGGREGATE_COL_WIDTH = 72;
// Fixed width for type badge column
const TYPE_BADGE_WIDTH = 60;

const TASK_TYPE_LABELS: Record<string, string> = {
  essay: 'Essay',
  activity: 'Activity',
  event: 'Event',
  multiple_choice: 'MCQ',
  oral_exam: 'Oral Exam',
};

const formatTaskTypeLabel = (taskType?: string) => {
  if (!taskType) return 'Task';
  return TASK_TYPE_LABELS[taskType] ?? taskType.replace(/_/g, ' ');
};

const toNumericScore = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const normalizeScorePercent = (score: number | null, maxScore: number | null): number | null => {
  if (score === null) return null;
  if (maxScore !== null && maxScore > 0) return Math.round((score / maxScore) * 100);
  if (score <= 1) return Math.round(score * 100);
  return Math.round(score);
};

interface McqScoreDetails {
  score: number | null;
  maxScore: number | null;
  scorePercent: number;
}

const getMcqScoreDetails = (task: Task, aggregatePerformance: number | null): McqScoreDetails | null => {
  const taskWithExtras = task as Task & {
    submission?: {
      finalGrade?: { score?: number; maxScore?: number };
      final_grade?: { score?: number; max_score?: number };
      mcqResult?: { score?: number; maxScore?: number };
      mcq_result?: { score?: number; max_score?: number };
    };
    mcqResult?: { score?: number; maxScore?: number };
    mcq_result?: { score?: number; max_score?: number };
  };

  const scoreCandidates: Array<{ score: number | null; maxScore: number | null }> = [
    {
      score: toNumericScore(taskWithExtras.submission?.finalGrade?.score),
      maxScore: toNumericScore(taskWithExtras.submission?.finalGrade?.maxScore),
    },
    {
      score: toNumericScore(taskWithExtras.submission?.final_grade?.score),
      maxScore: toNumericScore(taskWithExtras.submission?.final_grade?.max_score),
    },
    {
      score: toNumericScore(taskWithExtras.submission?.mcqResult?.score),
      maxScore: toNumericScore(taskWithExtras.submission?.mcqResult?.maxScore),
    },
    {
      score: toNumericScore(taskWithExtras.submission?.mcq_result?.score),
      maxScore: toNumericScore(taskWithExtras.submission?.mcq_result?.max_score),
    },
    {
      score: toNumericScore(taskWithExtras.mcqResult?.score),
      maxScore: toNumericScore(taskWithExtras.mcqResult?.maxScore),
    },
    {
      score: toNumericScore(taskWithExtras.mcq_result?.score),
      maxScore: toNumericScore(taskWithExtras.mcq_result?.max_score),
    },
  ];

  for (const candidate of scoreCandidates) {
    const normalized = normalizeScorePercent(candidate.score, candidate.maxScore);
    if (normalized !== null) {
      return {
        score: candidate.score,
        maxScore: candidate.maxScore,
        scorePercent: normalized,
      };
    }
  }

  if (aggregatePerformance !== null && !Number.isNaN(aggregatePerformance)) {
    return {
      score: null,
      maxScore: null,
      scorePercent: Math.round(aggregatePerformance * 100),
    };
  }

  return null;
};

const getMcqSummaryLine = (task: Task, aggregatePerformance: number | null): string | null => {
  if (task.type !== 'multiple_choice') return null;
  const mcqScore = getMcqScoreDetails(task, aggregatePerformance);
  if (!mcqScore) return 'Multiple choice task';

  if (mcqScore.score !== null && mcqScore.maxScore !== null && mcqScore.maxScore > 0) {
    return `Multiple choice task - ${mcqScore.score}/${mcqScore.maxScore} correct (${mcqScore.scorePercent}%)`;
  }

  return `Multiple choice task - Score ${mcqScore.scorePercent}%`;
};

interface TaskAttributeBadgeProps {
  icon: React.ElementType;
  hint: string;
}

const TaskAttributeBadge: React.FC<TaskAttributeBadgeProps> = React.memo(({ icon, hint }) => (
  <Tooltip
    placement="top"
    trigger={(triggerProps) => (
      <Box {...triggerProps} ml="$1" p="$1" opacity={0.5}>
        <Icon as={icon} size="xs" color="$textSubtle" />
      </Box>
    )}
  >
    <TooltipContent bg="$backgroundDark800" px="$2" py="$1" borderRadius="$md">
      <TooltipText>
        <Text color="$white" size="xs">
          {hint}
        </Text>
      </TooltipText>
    </TooltipContent>
  </Tooltip>
));

/**
 * Returns the dimension keys to use for the harvey-ball grid of a given task.
 * Oral exams have their own dimension list; everything else uses the essay/MCQ list.
 */
export const taskRowDimensionKeys = (
  task: Task,
  evalDimensionKeys: string[],
  oralExamDimensionKeys: string[],
): string[] => (
  isOralExamTask(task)
    ? resolveOralExamDimensionKeys(task, oralExamDimensionKeys)
    : evalDimensionKeys
);

/**
 * Compact column header above a *group* of task rows that share the same
 * dimension set. Repeats only when the dimension signature changes between
 * consecutive rows (e.g. essay block -> oral exam block).
 *
 * Layout mirrors the data row exactly so labels align under their balls.
 * Full dim names are used (no truncation): they are short enough at 2xs
 * and abbreviations like "ANAL" read poorly.
 */
export const TaskGroupDimensionHeader: React.FC<{
  dimensionKeys: string[];
}> = React.memo(({ dimensionKeys }) => {
  const { t } = useTranslation();

  return (
    <Box w="100%" maxWidth={1400} mt="$2">
      <HStack alignItems="flex-end" px="$4" pb="$1" space="md">
        {/* Spacer for chevron icon */}
        <Box w={24} />

        {/* Spacer for title column - matches flex={1} in data rows */}
        <HStack flex={1} />

        {/* Spacer for trophy icon column */}
        <Box w={24} />

        {/* Spacer for type badge column */}
        <Box w={TYPE_BADGE_WIDTH} />

        {/* Dimension and status columns - wrapped to match data row structure */}
        <HStack alignItems="center">
          {/* Dimension column headers (abbreviated) */}
          {dimensionKeys.map((dim) => (
            <Box key={dim} w={DIMENSION_COL_WIDTH} alignItems="center" justifyContent="center">
              <Text
                size="2xs"
                color="$textSubtle"
                fontWeight="bold"
                textTransform="uppercase"
                textAlign="center"
                numberOfLines={1}
              >
                {dim}
              </Text>
            </Box>
          ))}
          {/* Aggregate column header */}
          <Box w={AGGREGATE_COL_WIDTH} alignItems="center" justifyContent="center">
            <Text
              size="2xs"
              color="$textSubtle"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="center"
            >
              {t('lecturer.evalDesk.taskGradeColumn')}
            </Text>
          </Box>

          {/* Spacer for status icon column */}
          <Box w={28} />
        </HStack>
      </HStack>
    </Box>
  );
});

// Harvey ball size
const BALL_SIZE = 20;
const BALL_RADIUS = BALL_SIZE / 2;
const BALL_CENTER = BALL_SIZE / 2;

/**
 * SVG-based Harvey Ball for consistent rendering across all fill levels.
 * Uses pie-slice path for partial fills.
 * Performance of -1 means "not applicable" - shows nothing.
 */
const HarveyBall: React.FC<{
  performance: number; // 0-1 continuous value, or -1 for not applicable
  fillColor: string; // Already resolved hex color
  emptyColor: string; // Already resolved hex color for empty state
}> = React.memo(({ performance, fillColor, emptyColor }) => {
  // Not applicable - show empty space (no ball)
  if (performance < 0) {
    return <Box w={BALL_SIZE} h={BALL_SIZE} />;
  }

  // Map continuous 0-1 to discrete 0-4 levels
  const level = Math.round(performance * 4);

  // For empty state (0), show outline only
  if (level === 0) {
    return (
      <Svg width={BALL_SIZE} height={BALL_SIZE}>
        <Circle
          cx={BALL_CENTER}
          cy={BALL_CENTER}
          r={BALL_RADIUS - 1}
          fill="none"
          stroke={emptyColor}
          strokeWidth={1.5}
        />
      </Svg>
    );
  }

  // For full state, show filled circle
  if (level >= 4) {
    return (
      <Svg width={BALL_SIZE} height={BALL_SIZE}>
        <Circle cx={BALL_CENTER} cy={BALL_CENTER} r={BALL_RADIUS} fill={fillColor} />
      </Svg>
    );
  }

  // For partial fills, draw pie slice
  const fillPercent = level / 4;
  const angle = fillPercent * 360;
  const endAngle = (angle - 90) * (Math.PI / 180); // Start from top (-90 degrees)
  const startAngle = -90 * (Math.PI / 180);

  const x1 = BALL_CENTER + BALL_RADIUS * Math.cos(startAngle);
  const y1 = BALL_CENTER + BALL_RADIUS * Math.sin(startAngle);
  const x2 = BALL_CENTER + BALL_RADIUS * Math.cos(endAngle);
  const y2 = BALL_CENTER + BALL_RADIUS * Math.sin(endAngle);

  const largeArc = angle > 180 ? 1 : 0;

  const pathData = `M ${BALL_CENTER} ${BALL_CENTER} L ${x1} ${y1} A ${BALL_RADIUS} ${BALL_RADIUS} 0 ${largeArc} 1 ${x2} ${y2} Z`;

  return (
    <Svg width={BALL_SIZE} height={BALL_SIZE}>
      {/* Background circle outline */}
      <Circle
        cx={BALL_CENTER}
        cy={BALL_CENTER}
        r={BALL_RADIUS - 0.5}
        fill="none"
        stroke={emptyColor}
        strokeWidth={1}
      />
      {/* Filled pie slice */}
      <Path d={pathData} fill={fillColor} />
    </Svg>
  );
});

interface DimensionScoreGridProps {
  performances: Record<string, number>;
  aggregateDisplay: string | null;
  resolvedColors: Record<string, string>;
  emptyColor: string;
  evalDimensionKeys: string[];
  taskId: string;
}

/**
 * Grid of dimension scores in fixed columns.
 * Shows Harvey balls for each dimension and projected task grade (or MCQ points).
 */
const DimensionScoreGrid: React.FC<DimensionScoreGridProps> = React.memo(({
  performances,
  aggregateDisplay,
  resolvedColors,
  emptyColor,
  evalDimensionKeys,
  taskId,
}) => (
  <HStack alignItems="center">
    {evalDimensionKeys.map((dim) => (
      <Box key={dim} w={DIMENSION_COL_WIDTH} alignItems="center" justifyContent="center">
        <HarveyBall
          performance={performances[dim] ?? 0}
          fillColor={resolvedColors[dim]}
          emptyColor={emptyColor}
        />
      </Box>
    ))}
    <Box w={AGGREGATE_COL_WIDTH} alignItems="center" justifyContent="center">
      <Text
        size="sm"
        color={aggregateDisplay ? '$textHeading' : '$textHint'}
        fontWeight="semibold"
        testID={`task-score-${taskId}`}
      >
        {aggregateDisplay || '--'}
      </Text>
    </Box>
  </HStack>
));

interface ElementHeaderProps {
  task: Task;
  toggleElement: () => void;
  isExpanded: boolean;
  isDisabled?: boolean;
  formErrors?: Record<string, unknown>;
  formSubmitted?: boolean;
  isExpandable?: boolean;
  isGradingRelevant?: boolean;
  evaluationRequired?: boolean;
  applicableDims?: string[];
}

const ElementHeader: React.FC<ElementHeaderProps> = ({
  task,
  toggleElement,
  isExpanded,
  isDisabled = false,
  formErrors = {},
  formSubmitted,
  isExpandable = false,
  isGradingRelevant = true,
  evaluationRequired = true,
  applicableDims,
}) => {
  const { control } = useFormContext();
  const {
    levelWeights,
    dimensionMaxWeights,
    dimensionColors,
    evalDimensionKeys,
    oralExamDimensionKeys,
    resolveGradeProjector,
  } = useEvaluationGradingConfig();

  // Resolve color tokens to hex values - ALWAYS call hook (not conditionally)
  const gluestackConfig = useTenantSettingsStore((state) => state.gluestackConfig);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const themeColors = (gluestackConfig?.tokens as any)?.colors as Record<string, string> | undefined;

  const isOralExam = isOralExamTask(task);

  // Pick the dim keys that drive the row's score grid. Oral exams use their
  // own dimension list; everything else uses the essay/MCQ dimension list.
  const rowDimensionKeys = isOralExam
    ? resolveOralExamDimensionKeys(task, oralExamDimensionKeys)
    : evalDimensionKeys;

  const { resolvedColors, emptyColor } = React.useMemo(() => {
    const emptyColor = themeColors?.borderLight || '#E5E5E5';
    const fallbackColor = themeColors?.brandBase || '#666666';

    const resolved: Record<string, string> = {};
    rowDimensionKeys.forEach((dim) => {
      const colorToken = dimensionColors[dim];
      if (!colorToken) {
        resolved[dim] = fallbackColor;
      } else if (colorToken.startsWith('#')) {
        resolved[dim] = colorToken;
      } else {
        const tokenKey = colorToken.startsWith('$') ? colorToken.slice(1) : colorToken;
        resolved[dim] = themeColors?.[tokenKey] || fallbackColor;
      }
    });

    return { resolvedColors: resolved, emptyColor };
  }, [dimensionColors, rowDimensionKeys, themeColors]);

  // Create a set of applicable dimensions for this task (for showing empty vs filled).
  // For oral exams, applicableDims is computed from essay dim keys upstream, which
  // would incorrectly filter out the oral-exam-specific keys; treat all
  // oralExamDimensionKeys as applicable in that case.
  const applicableDimSet = React.useMemo(() => {
    if (isOralExam) return new Set(rowDimensionKeys);
    return new Set(applicableDims ?? []);
  }, [isOralExam, applicableDims, rowDimensionKeys]);

  // Watch the whole task eval (same path autosave uses). Watching
  // `.evaluation.dimensions` with defaultValue {} stays empty: RHF does not
  // register that parent object, only DimRating leaf `.level` fields — so the
  // title bar showed 0% / empty Harvey balls while the dots below were filled.
  const taskFormEval = useWatch({
    control,
    name: `taskEvaluations.${task.id}`,
  }) as TaskFormEvaluation | undefined;

  const evalData = React.useMemo(() => {
    if (taskFormEval && taskFormEval.kind === 'oral_exam') {
      return {};
    }
    if (taskFormEval && 'evaluation' in taskFormEval) {
      return taskFormEval.evaluation?.dimensions ?? {};
    }
    return getFlatEvaluationDimensions(task.submission?.evaluation);
  }, [taskFormEval, task.submission?.evaluation]);

  // Oral exam aggregation: per-dimension average of question level-weights.
  const oralSubEvaluations = useWatch({
    control,
    name: `taskEvaluations.${task.id}.subEvaluations`,
  }) as Record<string, { dimGrades?: Record<string, { level?: string | null }> }> | undefined;

  const oralPerformances = React.useMemo(() => {
    if (!isOralExam) return {};
    const sums: Record<string, { total: number; count: number }> = {};
    Object.values(oralSubEvaluations || {}).forEach((sub) => {
      Object.entries(sub?.dimGrades || {}).forEach(([dim, rating]) => {
        const level = rating?.level;
        if (!level || level === 'not_applicable') return;
        const weight = levelWeights[level];
        if (typeof weight !== 'number') return;
        if (!sums[dim]) sums[dim] = { total: 0, count: 0 };
        sums[dim].total += weight;
        sums[dim].count += 1;
      });
    });
    const result: Record<string, number> = {};
    Object.entries(sums).forEach(([dim, { total, count }]) => {
      if (count > 0) result[dim] = total / count;
    });
    return result;
  }, [isOralExam, oralSubEvaluations, levelWeights]);

  const performances = React.useMemo(
    () => (isOralExam ? oralPerformances : convertEvaluationsToPerformances(evalData, levelWeights)),
    [isOralExam, oralPerformances, evalData, levelWeights],
  );

  const oralAggregatePerformance = React.useMemo(() => {
    if (!isOralExam) return null;
    return calculateOralExamPerformance(oralSubEvaluations, levelWeights);
  }, [isOralExam, oralSubEvaluations, levelWeights]);

  const rawTaskPerformance = isOralExam
    ? oralAggregatePerformance
    : calculateTaskPerformance(performances, dimensionMaxWeights);
  const mcqScore = getMcqScoreDetails(task, rawTaskPerformance);
  const typeLabel = formatTaskTypeLabel(task.type);
  const mcqSummaryLine = getMcqSummaryLine(task, rawTaskPerformance);

  const aggregateDisplay = React.useMemo(() => {
    if (task.type === 'multiple_choice') {
      if (!mcqScore) {
        return null;
      }
      if (mcqScore.score != null && mcqScore.maxScore != null && mcqScore.maxScore > 0) {
        return `${mcqScore.score}/${mcqScore.maxScore}`;
      }
      if (mcqScore.scorePercent != null) {
        return `${mcqScore.scorePercent}%`;
      }
      return null;
    }

    const projector = resolveGradeProjector(task.type ?? 'essay');
    return formatProjectedGradeDisplay(rawTaskPerformance, projector);
  }, [task.type, mcqScore, rawTaskPerformance, resolveGradeProjector]);

  // For non-applicable dimensions, set performance to -1 (will show as empty/disabled)
  const adjustedPerformances = React.useMemo(() => {
    const adjusted: Record<string, number> = {};
    rowDimensionKeys.forEach((dim) => {
      if (task.type === 'multiple_choice') {
        adjusted[dim] = -1;
        return;
      }
      if (applicableDimSet.has(dim)) {
        adjusted[dim] = performances[dim] ?? 0;
      } else {
        adjusted[dim] = -1; // Mark as not applicable
      }
    });
    return adjusted;
  }, [rowDimensionKeys, applicableDimSet, performances, task.type]);

  const hasErrors = Object.keys(formErrors || {}).length > 0;
  const isGated = task.evaluation?.completionPolicyEffective === 'gated';
  const isAutoEval = task.evaluation?.mode === 'auto';
  const isInteractive = !isDisabled && isExpandable;

  // Calculate total width of dimension grid area for consistent layout
  const dimensionGridWidth = (rowDimensionKeys.length * DIMENSION_COL_WIDTH) + AGGREGATE_COL_WIDTH;

  return (
    <Pressable
      onPress={isInteractive ? toggleElement : null}
      px="$4"
      py="$3"
      $hover={{ bg: '$bgHover' }}
      bg={isExpanded ? '$bgSubtle' : 'transparent'}
    >
      <HStack alignItems="center" space="md">
        {/* Toggle Icon */}
        <Box w={24} alignItems="center" justifyContent="center">
          {!!isExpandable && (
            <Icon
              as={isExpanded ? ChevronDown : ChevronRight}
              size="md"
              color={isInteractive ? '$brandBase' : '$textDisabled'}
            />
          )}
        </Box>

        {/* Title - flexible width */}
        <VStack flex={1} space="xs">
          <HStack alignItems="center" space="sm">
            <Text
              size="sm"
              fontWeight="semibold"
              color={isDisabled || !evaluationRequired ? '$textDisabled' : '$textHeading'}
              numberOfLines={1}
              flexShrink={1}
            >
              {task.title}
            </Text>
            {/* Minor attribute icons - inline after title */}
            {!!isGated && <TaskAttributeBadge icon={Lock} hint="Gated task" />}
            {!!isAutoEval && <TaskAttributeBadge icon={Sparkles} hint="Auto-evaluated" />}
          </HStack>
          {!!mcqSummaryLine && (
            <Text
              size="2xs"
              color="$textHint"
              numberOfLines={1}
              testID={`mcq-summary-${task.id}`}
            >
              {mcqSummaryLine}
            </Text>
          )}
        </VStack>

        {/* Transfer task icon - fixed width column */}
        <Box w={24} alignItems="center">
          {!!isGradingRelevant && (
            <Icon as={Trophy} size="sm" color="$textHint" />
          )}
        </Box>

        {/* Type tag - fixed width, right-aligned */}
        <Box w={TYPE_BADGE_WIDTH} alignItems="flex-end">
          <Text
            size="2xs"
            color="$textHint"
            bg="$bgSubtle"
            px="$1.5"
            py="$0.5"
            borderRadius="$xs"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing="$sm"
          >
            {typeLabel}
          </Text>
        </Box>

        {/* Dimension score grid - ALWAYS render for consistent layout */}
        <HStack alignItems="center">
          {evaluationRequired ? (
            <DimensionScoreGrid
              performances={adjustedPerformances}
              aggregateDisplay={aggregateDisplay}
              resolvedColors={resolvedColors}
              emptyColor={emptyColor}
              evalDimensionKeys={rowDimensionKeys}
              taskId={task.id}
            />
          ) : (
            // Empty placeholder to maintain column alignment
            <Box w={dimensionGridWidth} />
          )}

          {/* Submission status icons */}
          <Box w={28} alignItems="center">
            {!!formSubmitted && evaluationRequired && (
              hasErrors ? (
                <Icon as={TriangleAlert} size="sm" color="$statusWarning" />
              ) : (
                <Icon as={Check} size="sm" color="$statusSuccess" />
              )
            )}
          </Box>
        </HStack>
      </HStack>
    </Pressable>
  );
};

export default React.memo(ElementHeader);
